import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AppShell } from "@/components/Nav";
import { Button } from "@/components/ui/button";
import { OnboardingWizard } from "@/components/OnboardingWizard";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkles,
  RefreshCw,
  Target,
  ArrowUpRight,
  Zap,
  TrendingUp,
  TrendingDown,
  Award,
  BookOpen,
  AlertTriangle,
  Rocket,
} from "lucide-react";

const IMPACT_STYLES = {
  high: {
    ring: "border-[#D5FF3F]/40 bg-gradient-to-br from-[#D5FF3F]/10 to-transparent",
    pill: "bg-[#D5FF3F] text-[#0B0C0E]",
    icon: Zap,
    tag: "High impact",
  },
  medium: {
    ring: "border-[#7CFF9B]/30 bg-gradient-to-br from-[#7CFF9B]/8 to-transparent",
    pill: "bg-[#7CFF9B]/15 text-[#7CFF9B]",
    icon: TrendingUp,
    tag: "Medium impact",
  },
  low: {
    ring: "border-[#26282C] bg-[#131518]",
    pill: "bg-[#26282C] text-[#A8ADA5]",
    icon: Target,
    tag: "Foundational",
  },
};

const RANK_LABEL = ["Move #1", "Move #2", "Move #3"];

function ScoreRerunBanner({ progress, prevTitles }) {
  if (!progress || (progress.entries_logged || 0) < 1) return null;
  const v = progress.verdict;
  const styles = {
    underperforming: {
      Icon: AlertTriangle,
      ring: "border-[#FF6B4A]/50 bg-gradient-to-br from-[#FF6B4A]/12 to-transparent",
      pill: "bg-[#FF6B4A]/20 text-[#FF6B4A]",
      title: "Coach re-ranked — new play needed.",
      tag: "Under projection",
    },
    overperforming: {
      Icon: Rocket,
      ring: "border-[#7CFF9B]/40 bg-gradient-to-br from-[#7CFF9B]/12 to-transparent",
      pill: "bg-[#7CFF9B]/20 text-[#7CFF9B]",
      title: "You&rsquo;re ahead. Coach doubled down.",
      tag: "Ahead of projection",
    },
    on_track: {
      Icon: TrendingUp,
      ring: "border-[#D5FF3F]/30 bg-gradient-to-br from-[#D5FF3F]/8 to-transparent",
      pill: "bg-[#D5FF3F]/15 text-[#D5FF3F]",
      title: "Coach refreshed with your latest score.",
      tag: "On track",
    },
  };
  const s = styles[v] || styles.on_track;
  const Icon = s.Icon;
  const deltaVsProj = progress.delta_vs_projected_low;
  return (
    <div
      className={`card-surface p-5 lg:p-6 mb-8 border ${s.ring}`}
      data-testid="score-rerun-banner"
    >
      <div className="flex items-start gap-4 flex-wrap">
        <div className="w-10 h-10 rounded-full bg-[#0B0C0E] border border-white/10 flex items-center justify-center flex-shrink-0">
          <Icon size={18} className={s.pill.split(" ").find((c) => c.startsWith("text-"))} />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span
              className={`text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest ${s.pill}`}
            >
              {s.tag}
            </span>
            {typeof progress.actual_delta === "number" && (
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C]">
                Actual {progress.actual_delta >= 0 ? "+" : ""}
                {progress.actual_delta} pts · Projected floor +
                {progress.projected_low_at_latest - progress.starting_score} pts
              </span>
            )}
          </div>
          <div
            className="font-display text-xl md:text-2xl leading-tight text-[#F5F3EE]"
            dangerouslySetInnerHTML={{ __html: s.title }}
          />
          {v === "underperforming" && (
            <p className="text-sm text-[#A8ADA5] mt-1.5 leading-relaxed">
              Your last log came in{" "}
              <span className="text-[#FF6B4A]">{Math.abs(deltaVsProj)} pts</span> under the
              projected floor. The coach swapped tactics — new top 3 below.
            </p>
          )}
          {v === "overperforming" && (
            <p className="text-sm text-[#A8ADA5] mt-1.5 leading-relaxed">
              You&rsquo;re{" "}
              <span className="text-[#7CFF9B]">
                +{progress.latest_score - progress.projected_high_at_latest} pts
              </span>{" "}
              above the projected ceiling. Lock in the win — top 3 below.
            </p>
          )}
          {v === "on_track" && (
            <p className="text-sm text-[#A8ADA5] mt-1.5 leading-relaxed">
              Landed inside the projected band. Coach kept the same category focus but sharpened the
              specific move.
            </p>
          )}
          {prevTitles.length > 0 && v !== "on_track" && (
            <div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[#6D716C]">
              Previously ranked ·{" "}
              {prevTitles.slice(0, 3).map((t, i) => (
                <span key={t} className="line-through mr-2">
                  {t}
                  {i < Math.min(prevTitles.length, 3) - 1 && " ·"}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TopTacticCard({ t, idx }) {
  const style = IMPACT_STYLES[t.impact] || IMPACT_STYLES.low;
  const Icon = style.icon;
  return (
    <div
      className={`card-surface p-6 lg:p-8 relative overflow-hidden border ${style.ring}`}
      data-testid={`top-tactic-${idx}`}
    >
      <div className="flex items-start justify-between mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="ticker text-5xl text-[#D5FF3F] leading-none">
            {String(idx + 1).padStart(2, "0")}
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C]">
              {RANK_LABEL[idx]}
            </div>
            <div className="text-xs font-mono uppercase tracking-widest text-[#A8ADA5]">
              {t.category}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span
            className={`text-[10px] px-2 py-0.5 rounded-full font-mono uppercase tracking-widest flex items-center gap-1 ${style.pill}`}
          >
            <Icon size={10} /> {style.tag}
          </span>
          {t.est_lift && (
            <span className="ticker text-sm text-[#D5FF3F]">{t.est_lift}</span>
          )}
        </div>
      </div>

      <h3 className="font-display text-3xl md:text-4xl leading-tight mb-4 text-[#F5F3EE]">
        {t.title}
      </h3>

      <div className="mb-5">
        <div className="overline mb-2">Why this, for you</div>
        <p className="text-sm text-[#A8ADA5] leading-relaxed">{t.reasoning}</p>
      </div>

      {t.action_route && (
        <Link to={t.action_route}>
          <Button
            className="rounded-full btn-lime h-11 px-6"
            data-testid={`top-tactic-action-${idx}`}
          >
            {t.action_label || "Take action"} <ArrowUpRight size={14} className="ml-1.5" />
          </Button>
        </Link>
      )}
    </div>
  );
}

function RunnerUpRow({ t, i }) {
  return (
    <div className="flex items-start gap-3 p-4 rounded-sm hover:bg-[#17191C] transition-colors">
      <div className="ticker text-xs text-[#6D716C] mt-0.5 flex-shrink-0">
        {String(i + 4).padStart(2, "0")}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-[#F5F3EE] text-sm">{t.title}</div>
        {t.category && (
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#6D716C] mt-0.5">
            {t.category}
          </div>
        )}
        {t.reasoning && (
          <div className="text-xs text-[#A8ADA5] mt-1.5 leading-relaxed">{t.reasoning}</div>
        )}
      </div>
      {t.action_route && (
        <Link
          to={t.action_route}
          className="flex-shrink-0 text-xs text-[#D5FF3F] font-mono hover:underline flex items-center gap-1"
        >
          {t.action_label || "Go"} <ArrowUpRight size={11} />
        </Link>
      )}
    </div>
  );
}

export default function Tips() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.get("/tips", { params: refresh ? { refresh: true } : {} });
      setData(data);
      if (refresh) toast.success("Coach refreshed");
      // Fire a one-shot toast the first time the user opens Coach after a score-log rerun.
      // Keyed by tips.created_at so each fresh rerun triggers exactly one notification.
      if (!refresh && data?.score_progress && data?.created_at) {
        const seen = localStorage.getItem("aura.tips_seen_at");
        if (seen !== data.created_at) {
          const v = data.score_progress.verdict;
          const msg =
            v === "underperforming"
              ? "Coach re-ranked — new top 3 based on your score"
              : v === "overperforming"
                ? "Coach doubled down — new top 3 to lock in your gains"
                : "Coach refreshed with your latest score";
          toast.success(msg, {
            duration: 6000,
            description: `Score progress: ${data.score_progress.actual_delta >= 0 ? "+" : ""}${data.score_progress.actual_delta} pts since starting`,
          });
          localStorage.setItem("aura.tips_seen_at", data.created_at);
        }
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(false);
  }, []);

  const top = data?.top_3 || [];
  const runners = data?.runner_ups || [];
  const snapshot = data?.user_snapshot || {};

  // If any onboarding step is incomplete, show the wizard instead of the tips list.
  const disp = snapshot.disputes_by_status || {};
  const onboardingComplete =
    (snapshot.negative_items || 0) > 0 &&
    ((disp.draft || 0) + (disp.sent || 0) + (disp.delivered || 0) + (disp.resolved || 0)) > 0 &&
    ((disp.sent || 0) + (disp.delivered || 0) + (disp.resolved || 0) +
      (snapshot.certified_mail_delivered || 0)) > 0;

  return (
    <AppShell>
      <div className="mb-10 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-3 text-[#D5FF3F]">Your personal coach</div>
          <h1 className="font-display text-5xl md:text-6xl leading-none">
            {onboardingComplete ? "Your top 3 moves." : "Welcome to Aura."}
          </h1>
          <p className="text-[#A8ADA5] mt-3 max-w-2xl leading-relaxed">
            {onboardingComplete ? (
              <>
                Aura&rsquo;s AI reads your actual file (
                {snapshot.negative_items ?? 0} negatives ·{" "}
                {(snapshot.disputes_by_status?.sent ?? 0) +
                  (snapshot.disputes_by_status?.delivered ?? 0)}{" "}
                disputes in play) and picks the exact playbook tactics that will move YOUR score
                the most — nothing generic.
              </>
            ) : (
              <>
                Three moves get you from zero to your first dispute in the mail. After that, the AI
                Coach takes over and personalizes everything.
              </>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/guide">
            <Button className="rounded-full btn-outline h-10 px-4" data-testid="coach-playbook-btn">
              <BookOpen size={13} className="mr-1.5" /> Full playbook
            </Button>
          </Link>
          <Button
            onClick={() => load(true)}
            disabled={loading}
            data-testid="tips-refresh-btn"
            className="rounded-full btn-outline h-10 px-4"
          >
            <RefreshCw size={13} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {/* Onboarding wizard (shows until all 3 steps done) */}
      {!loading && !onboardingComplete && <OnboardingWizard snapshot={snapshot} />}

      {/* Score-log-triggered rerun banner */}
      {!loading && onboardingComplete && top.length > 0 && data?.score_progress && (
        <ScoreRerunBanner
          progress={data.score_progress}
          prevTitles={data.previous_top3_titles || []}
        />
      )}

      {loading ? (
        <div className="card-surface p-10 text-center" data-testid="tips-loading">
          <Sparkles size={22} className="text-[#D5FF3F] mx-auto mb-3 animate-pulse" />
          <div className="font-display text-2xl mb-1">Reading your file…</div>
          <div className="text-sm text-[#A8ADA5]">
            AI is ranking playbook tactics against your specific data.
          </div>
        </div>
      ) : !onboardingComplete ? (
        /* wizard already rendered above */
        null
      ) : top.length > 0 ? (
        <>
          {/* Top 3 ranked */}
          <div className="grid gap-5 mb-12" data-testid="top-3-list">
            {top.map((t, i) => (
              <TopTacticCard key={`${i}-${t.title}`} t={t} idx={i} />
            ))}
          </div>

          {/* Runner-ups */}
          {runners.length > 0 && (
            <div className="card-surface p-6 mb-8" data-testid="runner-ups-list">
              <div className="flex items-center gap-2 mb-4">
                <Award size={16} className="text-[#A8ADA5]" />
                <div className="overline">Also worth doing</div>
              </div>
              <div className="divide-y divide-[#26282C]">
                {runners.map((t, i) => (
                  <RunnerUpRow key={`${i}-${t.title}`} t={t} i={i} />
                ))}
              </div>
            </div>
          )}

          <div className="text-[10px] text-[#6D716C] font-mono uppercase tracking-widest text-center">
            Personalized {data?.created_at ? new Date(data.created_at).toLocaleString() : ""} ·
            Cached 24h · Hit refresh after taking action for a re-rank
          </div>
        </>
      ) : (
        <div className="card-surface p-10 text-center" data-testid="tips-empty">
          <Target size={28} className="text-[#A8ADA5] mx-auto mb-4" />
          <div className="font-display text-2xl mb-2">Upload your report first.</div>
          <p className="text-sm text-[#A8ADA5] mb-6 max-w-md mx-auto">
            The coach needs to see your file to rank tactics. Start with an AI scan — takes 30
            seconds.
          </p>
          <Link to="/app/scan">
            <Button className="rounded-full btn-lime h-11 px-6">
              Run AI Scan <ArrowUpRight size={14} className="ml-1.5" />
            </Button>
          </Link>
        </div>
      )}
    </AppShell>
  );
}
